import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import 'dayjs/locale/fr';
import { useRouter } from 'next/router';
import settings from '@/settings';
import Sidebar from '@/components/blogs/sidebar';
import { getShortenCountryName } from '@/lib/use-origin-country';
import { WebsiteSchema } from '@/components/homepage/WebsiteSchema';
import blogs from '@/settings/blogs';
import { REDIRECT_HOME } from '@/lib/common';
import i18n from '@/settings/i18n';
import { formatStoreDate } from '@/lib/intl-format';

dayjs.extend(advancedFormat);
dayjs.extend(utc);

const t = i18n.init('blogs');

// French writes the day before the month with no ordinal ("5 janv. 2024").
// publishedAt is a calendar date at midnight UTC, so format it in UTC or
// timezones behind UTC show the day before.
const formatBlogDate = (date: string) => {
  const locale = i18n.getLocale();

  const published = dayjs.utc(date);

  return (
    formatStoreDate(published, 'order') ??
    published
      .locale(locale)
      .format(locale === 'fr' ? 'D MMM YYYY' : 'MMM Do YYYY')
  );
};

export const getServerSideProps = async () => {
  if (!blogs || !blogs?.articles) {
    return REDIRECT_HOME;
  }

  return { props: {} };
};

export default function Blogs() {
  const router = useRouter();

  if (!blogs) {
    return null;
  }

  const allArticles = blogs.articles;
  const [firstArticle] = allArticles;

  const tagged = router?.query?.tagged?.toString();

  return (
    <>
      <WebsiteSchema
        title={t`News - Reebelo ${{ country: getShortenCountryName() }}`}
        description={t`Reebelo ${{
          country: settings.country,
        }} is APAC's leading marketplace for sustainable tech with offices in ${{
          offices: settings.country_office_locations,
        }}. The positive customer experience at Reebelo ${{
          country: settings.country,
        }} is of utmost importance to us. Please check here for Reebelo ${{
          country: settings.country,
        }} News.`}
      />
      <div className="reebelo-container pb-6 pt-5 md:pb-20 md:pt-8">
        <h1 className="mb-5 text-left text-base font-semibold text-gray-700 xxs:text-lg xs:text-xl md:mb-7 md:text-2xl xl:text-3xl">
          {t`News`}
        </h1>
        <div className="flex flex-wrap gap-5 md:gap-8 lg:flex-nowrap">
          <div className="w-full lg:w-3/5 xl:w-[70%]">
            {!tagged && (
              <div className="mb-7 w-full md:mb-12">
                <Link href={`/blogs/${firstArticle.handle}`}>
                  <div className="w-full">
                    <Image
                      src={firstArticle.image}
                      alt={t`Banner`}
                      className="w-full rounded-lg object-cover duration-300 ease-in-out hover:scale-105 lg:hover:scale-[1.03]"
                      height={535}
                      width={990}
                    />
                  </div>
                  <h2 className="mt-4 text-xl font-bold !leading-tight text-gray-700 duration-150 ease-in-out hover:text-blue-500 sm:text-2xl">
                    {firstArticle.title}
                  </h2>
                </Link>
                <p className="mb-4 text-sm text-gray-700/75 md:text-base">
                  {formatBlogDate(firstArticle.publishedAt)}
                </p>
                <p className="text-sm text-gray-700 md:text-base">
                  {firstArticle.excerpt}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-7 md:grid-cols-2 md:gap-y-14">
              {allArticles
                .filter((e) =>
                  // Filter first item if tagged is false.
                  tagged ? e : e.handle !== firstArticle.handle,
                )
                .filter((e) =>
                  // Filter items based on tagged if tagged is true.
                  tagged ? e.tags.toString().toLowerCase().includes(tagged) : e,
                )
                .map((article) => (
                  <div className="w-full" key={article.handle}>
                    <Link href={`/blogs/${article.handle}`}>
                      {article?.image && (
                        <div className="aspect-video">
                          <Image
                            src={article.image}
                            alt={t`Blog image`}
                            className="h-full w-full rounded-lg object-cover duration-300 ease-in-out hover:scale-105"
                            height={260}
                            width={480}
                          />
                        </div>
                      )}
                      <h2 className="mt-4 text-lg font-bold !leading-tight text-gray-700 duration-150 ease-in-out hover:text-blue-500 sm:text-xl">
                        {article.title}
                      </h2>
                    </Link>
                    <p className="mb-4 text-sm text-gray-700 md:text-base">
                      {article.tags.length > 0 && (
                        <>
                          <span>{article.tags[0]}</span>
                          <span className="mx-2.5 inline-block h-1.5 w-1.5 rounded-full bg-gray-700"></span>
                        </>
                      )}
                      <span className="text-sm text-gray-700/75">
                        {formatBlogDate(article.publishedAt)}
                      </span>
                    </p>
                    <p className="text-sm text-gray-700 md:text-base">
                      {article.excerpt}
                    </p>
                  </div>
                ))}
            </div>
          </div>
          <div className="hidden w-full lg:block lg:w-2/5 xl:w-[30%]">
            <Sidebar articles={allArticles} />
          </div>
        </div>
      </div>
    </>
  );
}
